import React, { useState, useRef } from 'react';
import { Archive, Download, Upload, Loader2, AlertTriangle, CheckCircle2, ShieldAlert } from 'lucide-react';
import { backupAPI } from '../../services/adminApi';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'sonner';

const cyan = '#00F0FF';
const cardStyle = {
  background: 'rgba(30, 41, 59, 0.6)',
  border: '1px solid rgba(0, 240, 255, 0.2)',
  borderRadius: '16px',
};

const AdminBackup = () => {
  const { t } = useLanguage();
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [pendingBackup, setPendingBackup] = useState(null);
  const [pendingFileName, setPendingFileName] = useState('');
  const fileInputRef = useRef(null);

  const handleExport = async () => {
    setExporting(true);
    try {
      const data = await backupAPI.export();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const date = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `kartal-air-backup-${date}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      const total = Object.values(data.collections || {}).reduce((s, arr) => s + (arr?.length || 0), 0);
      toast.success(t('admin.backup.exportSuccess').replace('{count}', total));
    } catch (err) {
      toast.error(t('admin.backup.exportError') + ': ' + err.message);
    } finally {
      setExporting(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      if (!data || typeof data !== 'object' || !data.collections) {
        toast.error(t('admin.backup.invalidFile'));
        return;
      }
      setPendingBackup(data);
      setPendingFileName(file.name);
    } catch (err) {
      toast.error(t('admin.backup.invalidFile'));
    } finally {
      // reset so selecting the same file again re-triggers change
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const confirmImport = async () => {
    if (!pendingBackup) return;
    setImporting(true);
    try {
      const res = await backupAPI.import(pendingBackup);
      toast.success(t('admin.backup.importSuccess').replace('{count}', res.total_documents ?? 0));
      setPendingBackup(null);
      setPendingFileName('');
    } catch (err) {
      toast.error(t('admin.backup.importError') + ': ' + err.message);
    } finally {
      setImporting(false);
    }
  };

  const pendingCounts = pendingBackup
    ? Object.entries(pendingBackup.collections || {}).map(([k, v]) => ({ name: k, count: Array.isArray(v) ? v.length : 0 }))
    : [];
  const pendingTotal = pendingCounts.reduce((s, c) => s + c.count, 0);

  return (
    <div className="space-y-8" data-testid="admin-backup-page">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <Archive size={28} style={{ color: cyan }} />
          <h1 className="text-3xl font-bold" style={{ color: '#F8FAFC' }}>
            {t('admin.backup.title')}
          </h1>
        </div>
        <p className="text-base" style={{ color: '#94A3B8' }}>
          {t('admin.backup.subtitle')}
        </p>
      </div>

      {/* Info banner */}
      <div className="flex items-start gap-3 p-4 rounded-xl" style={{
        background: 'rgba(0, 240, 255, 0.08)',
        border: '1px solid rgba(0, 240, 255, 0.2)',
      }}>
        <ShieldAlert size={20} style={{ color: cyan, marginTop: 2 }} />
        <p className="text-sm" style={{ color: '#CBD5E1' }}>{t('admin.backup.info')}</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Export card */}
        <div className="p-6" style={cardStyle} data-testid="backup-export-card">
          <div className="flex items-center gap-3 mb-3">
            <Download size={22} style={{ color: cyan }} />
            <h2 className="text-lg font-semibold" style={{ color: '#F8FAFC' }}>
              {t('admin.backup.exportTitle')}
            </h2>
          </div>
          <p className="text-sm mb-6" style={{ color: '#94A3B8' }}>
            {t('admin.backup.exportDesc')}
          </p>
          <button
            onClick={handleExport}
            disabled={exporting}
            data-testid="backup-export-button"
            className="flex items-center gap-2 px-5 py-3 rounded-lg font-medium transition-all duration-300 disabled:opacity-60"
            style={{ background: cyan, color: '#0F172A' }}
          >
            {exporting ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />}
            {exporting ? t('admin.backup.exporting') : t('admin.backup.exportButton')}
          </button>
        </div>

        {/* Import card */}
        <div className="p-6" style={cardStyle} data-testid="backup-import-card">
          <div className="flex items-center gap-3 mb-3">
            <Upload size={22} style={{ color: '#F59E0B' }} />
            <h2 className="text-lg font-semibold" style={{ color: '#F8FAFC' }}>
              {t('admin.backup.importTitle')}
            </h2>
          </div>
          <p className="text-sm mb-6" style={{ color: '#94A3B8' }}>
            {t('admin.backup.importDesc')}
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json,.json"
            onChange={handleFileSelect}
            className="hidden"
            data-testid="backup-import-file-input"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            data-testid="backup-import-select-button"
            className="flex items-center gap-2 px-5 py-3 rounded-lg font-medium transition-all duration-300 disabled:opacity-60"
            style={{ background: 'transparent', color: '#F59E0B', border: '1px solid rgba(245, 158, 11, 0.5)' }}
          >
            <Upload size={18} />
            {t('admin.backup.selectFile')}
          </button>
        </div>
      </div>

      {/* Confirmation modal */}
      {pendingBackup && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}
          data-testid="backup-confirm-modal"
        >
          <div className="w-full max-w-md p-6 rounded-2xl" style={{
            background: '#1E293B',
            border: '1px solid rgba(245, 158, 11, 0.4)',
          }}>
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle size={26} style={{ color: '#F59E0B' }} />
              <h3 className="text-xl font-bold" style={{ color: '#F8FAFC' }}>
                {t('admin.backup.confirmTitle')}
              </h3>
            </div>
            <p className="text-sm mb-4" style={{ color: '#CBD5E1' }}>
              {t('admin.backup.confirmWarning')}
            </p>
            <div className="rounded-lg p-3 mb-5 max-h-48 overflow-y-auto" style={{ background: 'rgba(15, 23, 42, 0.6)' }}>
              <p className="text-xs mb-2" style={{ color: '#94A3B8' }}>
                {pendingFileName} — {t('admin.backup.totalRecords').replace('{count}', pendingTotal)}
              </p>
              {pendingCounts.map((c) => (
                <div key={c.name} className="flex justify-between text-xs py-0.5" style={{ color: '#CBD5E1' }}>
                  <span>{c.name}</span>
                  <span style={{ color: cyan }}>{c.count}</span>
                </div>
              ))}
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => { setPendingBackup(null); setPendingFileName(''); }}
                disabled={importing}
                data-testid="backup-confirm-cancel-button"
                className="px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-60"
                style={{ background: 'transparent', color: '#94A3B8', border: '1px solid rgba(148,163,184,0.3)' }}
              >
                {t('admin.backup.cancel')}
              </button>
              <button
                onClick={confirmImport}
                disabled={importing}
                data-testid="backup-confirm-restore-button"
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-60"
                style={{ background: '#F59E0B', color: '#0F172A' }}
              >
                {importing ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                {importing ? t('admin.backup.restoring') : t('admin.backup.confirmRestore')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBackup;
